import { useState } from "react";
import { ArrowUpRight, Check, Download, Package } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import BuyModal from "@/components/BuyModal";

const VARIANTS = [
  {
    id: "butterfly_effect_digital",
    label: "Digital",
    price: 49.99,
    tagline: "Instant access · PDF + EPUB",
    perks: [
      "Instant download",
      "Read on any device",
      "Lifetime access",
    ],
    icon: Download,
    physical: false,
  },
  {
    id: "butterfly_effect_physical",
    label: "Signed Hardcover",
    price: 99.99,
    tagline: "Personally signed · Free shipping",
    perks: [
      "Hand-signed by Cameron",
      "Free worldwide shipping",
      "Includes digital copy",
    ],
    icon: Package,
    physical: true,
    badge: "Most loved",
  },
];

const BOOK = {
  title: "The Butterfly Effect",
  subtitle: "The Hidden Framework Behind The Greatest Brands in History",
  year: "2026",
  status: "Available",
  tagline: "He taught you to think different. Now he shows you how.",
  pitch:
    "What's the real killer of businesses — bankruptcy, or obscurity? The Butterfly Effect is the hidden framework behind the greatest brands in history — and the playbook for the founders building the next ones.",
  cover:
    "https://customer-assets.emergentagent.com/job_cameron-portfolio/artifacts/ug5rctxi_PStudio_05022026%20%281%29.png",
  href: "#",
};

export default function Publications() {
  const [buyOpen, setBuyOpen] = useState(false);
  const [initialVariant, setInitialVariant] = useState(null);

  const openBuy = (variantId) => {
    setInitialVariant(variantId || null);
    setBuyOpen(true);
  };

  return (
    <main
      data-testid="publications-page"
      className="relative bg-[#0A0A0C] text-[#F5F5F5] min-h-screen overflow-x-hidden"
    >
      <Nav />

      {/* Featured Book */}
      <section
        data-testid="publications-featured-book"
        className="relative bg-black border-b border-white/10 pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 w-[80%] h-[400px] opacity-50"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(220,38,38,0.18) 0%, transparent 60%)",
          }}
        />
        <div className="relative mx-auto max-w-[1760px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Book mockup */}
            <div className="lg:col-span-6 relative flex items-center justify-center">
              <div
                aria-hidden
                className="absolute inset-0 opacity-70"
                style={{
                  background:
                    "radial-gradient(60% 60% at 50% 50%, rgba(220,38,38,0.25) 0%, transparent 65%)",
                  filter: "blur(40px)",
                }}
              />
              <img
                src={BOOK.cover}
                alt={BOOK.title}
                draggable={false}
                data-testid="featured-book-cover"
                className="relative w-full max-w-[640px] h-auto select-none"
                style={{ filter: "drop-shadow(0 40px 80px rgba(0,0,0,0.7))" }}
              />
            </div>

            {/* Book copy */}
            <div className="lg:col-span-6 flex flex-col">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">
                {BOOK.year} · Hardcover · 248 pages
              </span>
              <h3
                className="mt-4 font-display font-black tracking-tighter text-4xl md:text-6xl lg:text-7xl text-white leading-[0.92]"
                data-testid="featured-book-title"
              >
                The Butterfly{" "}
                <span
                  className="font-script italic font-normal whitespace-nowrap"
                  style={{
                    background:
                      "linear-gradient(180deg, #ffffff 0%, #FCA5A5 30%, #DC2626 65%, #7F1D1D 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    color: "transparent",
                    paddingBottom: "0.18em",
                    paddingRight: "0.08em",
                    display: "inline-block",
                    lineHeight: 1,
                  }}
                >
                  Effect.
                </span>
              </h3>
              <p className="mt-3 text-base md:text-lg text-white/70 max-w-xl leading-relaxed">
                {BOOK.subtitle}
              </p>

              <p className="mt-7 text-base md:text-lg text-white/65 max-w-xl leading-relaxed">
                {BOOK.pitch}
              </p>

              <blockquote className="mt-8 pl-5 border-l-2 border-[#DC2626]/60 max-w-xl">
                <p className="font-script italic text-2xl md:text-3xl text-white/85 leading-tight">
                  "{BOOK.tagline}"
                </p>
              </blockquote>

              {/* Variant cards — visible on the page (compact) */}
              <div
                className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md"
                data-testid="featured-book-variants"
              >
                {VARIANTS.map((v) => {
                  const Icon = v.icon;
                  return (
                    <div
                      key={v.id}
                      data-testid={`book-variant-${v.id}`}
                      className={`group relative rounded-xl p-4 border flex flex-col gap-3 ${
                        v.physical
                          ? "border-[#DC2626]/40 bg-gradient-to-br from-[#1A0608] via-[#0F0405] to-black shadow-[0_0_28px_rgba(220,38,38,0.10)]"
                          : "border-white/12 bg-white/[0.02]"
                      }`}
                    >
                      {v.badge ? (
                        <span className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-full bg-[#DC2626]/15 border border-[#DC2626]/40 text-[8px] font-mono uppercase tracking-[0.28em] text-[#FCA5A5]">
                          {v.badge}
                        </span>
                      ) : null}

                      <div className="flex items-center gap-2.5">
                        <div className="inline-flex items-center justify-center h-8 w-8 shrink-0 rounded-lg border border-white/10 bg-white/[0.04]">
                          <Icon className="h-3.5 w-3.5 text-[#FCA5A5]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-display font-black tracking-tight text-sm text-white leading-tight">
                            {v.label}
                          </h4>
                          <p className="text-[10px] text-white/55 leading-tight">
                            {v.tagline}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-baseline gap-1.5">
                        <span
                          className="font-display font-black tracking-tighter text-2xl"
                          style={{
                            background:
                              "linear-gradient(180deg, #ffffff 0%, #FCA5A5 60%, #DC2626 100%)",
                            WebkitBackgroundClip: "text",
                            backgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            color: "transparent",
                          }}
                        >
                          ${v.price.toFixed(2)}
                        </span>
                        <span className="font-mono text-[9px] uppercase tracking-[0.28em] text-white/45">
                          USD
                        </span>
                      </div>

                      <ul className="flex flex-col gap-1">
                        {v.perks.map((p) => (
                          <li
                            key={p}
                            className="flex items-start gap-1.5 text-[11px] text-white/70 leading-snug"
                          >
                            <Check className="h-3 w-3 mt-0.5 shrink-0 text-[#FCA5A5]" />
                            {p}
                          </li>
                        ))}
                      </ul>

                      <button
                        type="button"
                        onClick={() => openBuy(v.id)}
                        data-testid={`book-buy-${v.id}`}
                        className={`mt-1 group/btn inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-full text-[10px] font-mono font-semibold uppercase tracking-[0.2em] whitespace-nowrap transition-all ${
                          v.physical
                            ? "cta-clay-blue text-white"
                            : "border border-white/20 text-white/85 hover:text-white hover:border-white/40 bg-white/[0.02]"
                        }`}
                      >
                        <span className="relative z-10">
                          Buy {v.label.toLowerCase()}
                        </span>
                        <ArrowUpRight className="relative z-10 h-3 w-3 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.28em] text-white/45">
                <span className="h-px w-8 bg-white/15" />
                Secure payment by Stripe
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <BuyModal
        open={buyOpen}
        onOpenChange={setBuyOpen}
        initialVariantId={initialVariant}
      />
    </main>
  );
}
